import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useParams, Link } from 'react-router-dom';
import { categoryData } from '@/mocks/categoryData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const cat = slug && categoryData[slug as keyof typeof categoryData];

  if (!cat) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-2">分类未找到</h1>
            <Link to="/" className="text-primary-500 hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `韩漫在线观看 - ${cat.name}分类`,
    description: cat.description,
    url: `http://adeled.com/category/${slug}`,
    mainEntity: {
      '@type': 'ItemList',
      itemListElement: cat.comics.map((c, i) => ({
        '@type': 'ListItem',
        position: i + 1,
        item: {
          '@type': 'Book',
          name: c.title,
          author: { '@type': 'Person', name: c.author },
        },
      })),
    },
  };

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'http://adeled.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: cat.name,
        item: `http://adeled.com/category/${slug}`,
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(jsonLdBreadcrumb)}</script>

        <section className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
          <img
            src={cat.heroImageUrl}
            alt={`韩漫在线观看 - ${cat.name}`}
            title={`免费韩国漫画 - ${cat.name}分类`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-full max-w-3xl mx-auto px-4 text-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-background-50 mb-3">
                {cat.name}
              </h1>
              <p className="text-sm md:text-base text-background-50/70 max-w-xl mx-auto">
                {cat.description}
              </p>
            </div>
          </div>
        </section>

        <nav className="bg-background-50 border-b border-background-200/70">
          <div className="w-full px-4 md:px-6 py-3">
            <div className="max-w-6xl mx-auto flex items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="hover:text-primary-500 transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-800 font-medium">{cat.name}</span>
            </div>
          </div>
        </nav>

        <section className="bg-background-100 py-10 md:py-14">
          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="mb-8 md:mb-10">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                  {cat.name}
                </h2>
                <p className="text-sm text-foreground-500 mt-1">共 {cat.comics.length} 部作品</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {cat.comics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/comic/${comic.id}`}
                    className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300"
                  >
                    <div className="relative h-72 overflow-hidden">
                      <img
                        src={comic.coverUrl}
                        alt={comic.title}
                        title={`韩漫在线观看 - ${comic.title}`}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary-500 text-background-50 text-xs font-medium">
                        {comic.rating}分
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="text-base font-heading font-bold text-foreground-950 mb-1 group-hover:text-primary-600 transition-colors">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mb-2">{comic.author}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-accent-600 font-medium">{comic.latestChapter}</span>
                        <span className="text-xs text-foreground-400">
                          <i className="ri-eye-line mr-1"></i>{comic.views}
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}